import { safeGetItem, safeSetItem } from './safeStorage';

export let META_PIXEL_ID = '';
export const META_PIXEL_READY_EVENT = 'jlastore:meta-pixel-ready';
let initializedFbq = null;
const initializedIds = new Set();
let lastPageView = null;

const notifyPixelReady = () => window.dispatchEvent(new Event(META_PIXEL_READY_EVENT));
export const configureMetaPixel = (pixel) => {
  const id = pixel?.enabled && /^\d{5,25}$/.test(pixel.id) ? pixel.id : '';
  if (id !== META_PIXEL_ID) { META_PIXEL_ID = id; lastPageView = null; }
  notifyPixelReady();
};

// Keep initialization and events together so deferred scripts cannot race checkout.
const getPixel = () => {
  if (!META_PIXEL_ID || typeof window === 'undefined' || typeof document === 'undefined') return null;

  if (typeof window.fbq !== 'function') {
    const fbq = function () {
      if (fbq.callMethod) fbq.callMethod.apply(fbq, arguments);
      else fbq.queue.push(arguments);
    };
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];
    window.fbq = fbq;
    if (!window._fbq) window._fbq = fbq;
  }

  const fbq = window.fbq;
  // React owns route PageViews; otherwise the SDK also tracks pushState/popstate.
  fbq.disablePushState = true;
  if (initializedFbq !== fbq) { initializedIds.clear(); initializedFbq = fbq; }
  if (!initializedIds.has(META_PIXEL_ID)) {
    // autoConfig does not disable saved Event Setup Tool URL/selector rules.
    // Our verified invoice event owns Purchase, so opt out of those plugins too.
    fbq('set', 'autoConfig', false, META_PIXEL_ID);
    fbq('optOut', META_PIXEL_ID, 'ESTRuleEngine');
    fbq('optOut', META_PIXEL_ID, 'IWLParameters');
    fbq('init', META_PIXEL_ID);
    initializedIds.add(META_PIXEL_ID);
  }

  if (!document.querySelector('script[src="https://connect.facebook.net/en_US/fbevents.js"]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.addEventListener('load', notifyPixelReady);
    script.addEventListener('error', () => script.remove(), { once: true });
    document.head.appendChild(script);
  }

  return fbq;
};

export const trackMetaPageView = (route) => {
  if (!route || lastPageView === route) return false;
  try {
    const fbq = getPixel();
    // A queued PageView would use the URL at SDK load, which may be a different route.
    if (!fbq || typeof fbq.callMethod !== 'function') return false;
    // Meta reads the current browser URL at dispatch; do not substitute the home canonical URL.
    fbq('trackSingle', META_PIXEL_ID, 'PageView');
    lastPageView = route;
    return true;
  } catch {
    // Analytics failures must never interrupt navigation or payments.
    return false;
  }
};

const textValue = (value) => (
  typeof value === 'string' || typeof value === 'number' ? String(value).trim() : ''
);

export const buildMetaPurchase = (transaction) => {
  if (!transaction || textValue(transaction.payment_status ?? transaction.paymentStatus).toLowerCase() !== 'success') {
    return null;
  }
  // Funding a wallet is not a sale: count the later product purchase, not both.
  if (textValue(transaction.order_type).toLowerCase() === 'topup') return null;

  const transactionId = textValue(transaction.tran_id ?? transaction.tranId);
  const rawAmount = transaction.price ?? transaction.amount;
  const isDecimal = typeof rawAmount === 'number'
    || (typeof rawAmount === 'string' && /^(?:\d+\.?\d*|\.\d+)$/.test(rawAmount.trim()));
  const value = isDecimal ? Number(rawAmount) : NaN;
  if (!transactionId || !Number.isFinite(value) || value <= 0) return null;

  const itemId = textValue(transaction.item_id);
  const gameId = textValue(transaction.game_id);
  const itemTitle = textValue(transaction.item_title ?? transaction.itemTitle);
  const gameName = textValue(transaction.game_name ?? transaction.gameName);

  return {
    eventID: `jlastore.purchase.${transactionId}`,
    transactionId,
    parameters: {
      value,
      // /payments/status price/amount is always base USD, even when the gateway charged KHR.
      currency: 'USD',
      order_id: transactionId,
      num_items: 1,
      ...(itemId ? {
        content_type: 'product',
        content_ids: [itemId],
        contents: [{ id: itemId, quantity: 1, item_price: value }],
      } : {}),
      ...(itemTitle ? { content_name: itemTitle, package: itemTitle } : {}),
      ...(gameId ? { game_id: gameId } : {}),
      ...(gameName ? { content_category: gameName } : {}),
    },
  };
};

export const trackMetaPurchase = (transaction) => {
  const purchase = buildMetaPurchase(transaction);
  if (!purchase) return false;

  const storageKey = `jlastore.meta.${META_PIXEL_ID}.purchase.${encodeURIComponent(purchase.transactionId)}`;
  if (safeGetItem(storageKey) === 'sent') return false;

  try {
    const fbq = getPixel();
    // Do not permanently deduplicate an event sitting in a stub whose SDK may never load.
    // The mounted invoice retries on META_PIXEL_READY_EVENT, not on an unrelated page.
    if (!fbq || typeof fbq.callMethod !== 'function') return false;
    fbq('trackSingle', META_PIXEL_ID, 'Purchase', purchase.parameters, { eventID: purchase.eventID });
    // Survives polls, React remounts and refreshes in this browser. eventID also stays stable.
    safeSetItem(storageKey, 'sent');
    return true;
  } catch {
    return false;
  }
};
